import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItem';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { CustomHeaderProps } from 'ag-grid-react';
import { isUndefined } from 'lodash';
import { MouseEvent, useCallback, useEffect, useState } from 'react';

export interface MenuHeaderProps extends CustomHeaderProps {
  selected?: string;
  menuOptionValues: unknown[];
  menuOptionLabels: string[];
  displaySelectedLabel?: boolean;
  onChange?: (value: unknown) => void;
}

// Functional component for the actual UI
const MenuHeaderComponent = (props: MenuHeaderProps) => {
  const { menuOptionLabels, menuOptionValues, selected, onChange, displayName, displaySelectedLabel = true } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(!isUndefined(selected) ? menuOptionValues.indexOf(selected) : 0);
  const open = Boolean(anchorEl);

  // Sync selectedIndex when selected prop changes
  useEffect(() => {
    const newIndex = !isUndefined(selected) ? menuOptionValues.indexOf(selected) : 0;
    setSelectedIndex(newIndex);
  }, [selected, menuOptionValues]);

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (event: MouseEvent<HTMLElement>, index: number) => {
    setSelectedIndex(index);
    if (onChange) onChange(menuOptionValues[index]);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderSelectedLabel = useCallback(() => {
    if (!displaySelectedLabel) return null;
    if (selectedIndex >= 0 && selectedIndex < menuOptionLabels.length) {
      return ` (${menuOptionLabels[selectedIndex]})`;
    }
    return '';
  }, [displaySelectedLabel, menuOptionLabels, selectedIndex]);

  return (
    <Box>
      <List component='nav' aria-label='Display Mode'>
        <ListItemButton
          id='lock-button'
          aria-haspopup='listbox'
          aria-controls='lock-menu'
          aria-label='when device is locked'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickListItem}
        >
          {displayName} {displaySelectedLabel && renderSelectedLabel()} <ArrowDropDownIcon />
        </ListItemButton>
      </List>
      <Menu
        id='lock-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'lock-button',
            role: 'listbox',
          },
        }}
      >
        {menuOptionLabels.map((option, index) => (
          <MenuItem
            key={option}
            selected={index === selectedIndex}
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default MenuHeaderComponent;
